<template>
  <fts-client-modal 
    :title="title"
    :description="description"
    @closed="reset"
    ref="ftsClientModal">
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['fas', 'user-plus']" fixed-width />
    <!-- body -->
    <template #body>
      <div class="comfort-form-design">
        <div class="fts-form-group">
          <div class="fts-form-label">To</div>
          <div class="fts-form-control textarea invite-box fts-scrollbar fts-scrollbar-overlay" 
            :class="{focus: inputFocused}" @click="focus">
            <div class="invite-placeholder" v-if="invitePlaceholderVisible">name@example.com</div>
            <div class="invite-box-field ">
              <div 
                class="invite-email" 
                :class="{error: !x.valid}"
                v-for="x in inviteEmails" 
                :key="x.id">
                <div>{{x.email}}</div>
                <font-awesome-icon :icon="['fas', 'times']" class="remove-email" @click="removeEmail(x)" />
              </div>
              <input 
                type="text"
                v-model="inviteInput" 
                @focus="inputFocused=true" 
                @blur="inputFocused=false;insertEmail()" 
                @keyup="inviteInputKeyup"
                @keydown.delete="inputDelete"
                @keydown.space.prevent
                @keydown.188.prevent
                @paste.prevent="inviteInputOnPaste"
                autocomplete="inviteInput"
                ref="inviteInput">
            </div>
          </div>
          <div class="fts-form-error" v-if="emailError">
            Invalid email format.
          </div>
        </div>
        <!-- invitation link -->
        <div class="fts-form-group">
          <div class="fts-form-label">Invitation link</div>
          <div class="invite-link-form-control" v-if="invitationLink">
            <div class="invite-link-copy">
              {{invitationLink}}
              <div class="invite-link-copy-hint" v-if="linkCopied">Copied</div>
            </div>
            <fts-btn
              icon
              text
              v-clipboard:copy="invitationLink"
              v-clipboard:success="clipboardOnCopy"
              v-clipboard:error="clipboardOnError"
            >
              <font-awesome-icon :icon="['far', 'clone']" />
            </fts-btn>
          </div>
          <fts-btn
            outline
            color="primary"
            text-transform-init
            :loading="invitationLinkLoading"
            @click="getInvitationLink"
            v-else
          >
            Get Invitation Link
          </fts-btn>
        </div>
      </div>
      <!-- feedback indicate -->
      <fts-feedback-indicate modal ref="ftsFeedbackIndicate" />
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <fts-btn
        text
        :disabled="loading"
        @click="close"
      >
        <font-awesome-icon :icon="['fas', 'times']" /> Close
      </fts-btn>

      <fts-btn
        color="primary"
        :loading="loading"
        :disabled="disabledSend"
        @click.native.prevent="submit"
      >
        <font-awesome-icon :icon="['fas', 'check']" /> Send
      </fts-btn>
    </template>
  </fts-client-modal>
</template>

<script>
import Vue from 'vue';
import VueClipboard from 'vue-clipboard2';
Vue.use(VueClipboard);

import commons from '../commons/commons';
import { emailValidator } from "../commons/validator";
import { clientStore } from '../store/clientStore';
import { authorizationStore } from '../store/authorizationStore';

import FTSClientModal from "../elements/FTSClientModal";
import FTSBtn from "../elements/FTSBtn";
import FTSFeedbackIndicate from "../elements/FTSFeedbackIndicate";

let inviteInputCache = null;

export default {
  components: {
    "fts-client-modal": FTSClientModal,
    "fts-btn": FTSBtn,
    "fts-feedback-indicate": FTSFeedbackIndicate,
  },
  data: () => ({
    loading: false,
    error: false,
    inputFocused: false,
    inviteInput: null,
    inviteEmails: [],
    invitationLink: null,
    invitationLinkLoading: false,
    linkCopied: false,
    feedbackIndicateOption: {},
  }),
  methods: {
    open() {
      this.$refs.ftsClientModal.show();
      this.$nextTick(() => {
        this.focus();
      });
    },
    close() {
      this.$refs.ftsClientModal.close();
    },
    reset() {
      this.inviteInputCache = null;
      this.inviteInput = null;
      this.inviteEmails = [];
    },
    async getInvitationLink() {
      this.invitationLinkLoading = true;
      try {
        this.invitationLink = await clientStore.dispatch('getInvitationLink', { });
      } catch(err) {
        console.error(err);
      } finally {
        this.invitationLinkLoading = false;
      }
    },
    focus() {
      this.$refs.inviteInput.focus();
    },
    inviteInputKeyup(evt) {
      if ([13, 32, 188].some(x => x === evt.keyCode)) 
        this.insertEmail()
    },
    inviteInputOnPaste(evt) {
      try {
        let emails = evt.clipboardData.getData('text/plain');
        emails = emails.replaceAll(/\s/g,'').split(",");
        emails.forEach(x => this.insertEmail(x));
      } catch(err) {}
    },
    insertEmail(data) {
      const email = this.inviteInput || data 
      if (email && email.length && !this.inviteEmails.some(x => x.email === email)) {
        this.inviteEmails.push({
          id: commons.nanoID(),
          email: email,
          valid: emailValidator(email),
        })
        this.inviteInput = null
      }
    },  
    removeEmail(data) {     
      const index = data ? this.inviteEmails.indexOf(data) : -1
      this.inviteEmails.splice(index, 1);
    },
    inputDelete() {
      inviteInputCache = this.inviteInput
      if (!this.inviteInput && !inviteInputCache && this.inviteEmails && this.inviteEmails.length) 
        this.removeEmail()
    },
    clipboardOnCopy() {
      if (!this.linkCopied) {
        this.linkCopied = true
        setTimeout(() => {
          this.linkCopied = false
        }, 2000)
      }
    },
    clipboardOnError() {
      console.error("ClipboardOnError");
    },
    async submit() {
      this.loading = true
      try {
        let result = await clientStore.dispatch('sendInvitation', {
          emails: [...this.inviteEmails.map(x => x.email)]
        });
        this.$refs.ftsFeedbackIndicate.show({
          type: 'success',
          desc: result.data && result.data.message? result.data.message : "Invitation successfully send.",
          handler: () => {
            this.close()
          },
        })
      } catch(err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: 'error',
          desc: 'Invitation failed to send, please try again later.',
          response: err.response,
        });
        console.error(err);
      } finally {    
        this.loading = false;
      }
    }
  },
  computed: {
    title() {
      return `Send invitations to ${clientStore.getters.workspaceName || 'Inistate'}`
    },
    description() {
      return `New members will be able to see all of ${clientStore.getters.workspaceName || 'Inistate'}'s modules that have been shared in the workspace.`
    },
    invitePlaceholderVisible() {
      return !this.inviteInput && !this.inviteEmails.length
    },
    emailError() {
      return this.inviteEmails.filter(x => !x.valid ).length && authorizationStore.state.userProfile.userName != 'admin'
    },
    disabledSend() {
      return !this.inviteInput && (this.inviteEmails.length === 0 || this.emailError) ? true : false
    }
  }
}
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.invite-box {
  min-height: 88px;
  position: relative;
  &.focus {
    border-color: $primaryColor;
    outline: none;
    box-shadow: $inputBoxShadow;
    -webkit-box-shadow: $inputBoxShadow;
  }
  .invite-box-field {
    @include flex-gap($gap: 0.375rem);
    > input {
      flex: 1;
      padding: .375rem 0;
      border: 0;
      font-size: 1rem;
      background: inherit;
      color: inherit;
    }
  }
  .invite-placeholder {
    position: absolute;
    padding: .375rem 0;
    color: var(--fts-form-control-placeholder);
    line-height: 1.15;
  }
  .invite-email {
    display: flex;
    align-items: center;
    padding: .375rem .575rem;
    border-radius: .25rem;
    background: rgba($color: $primaryColor, $alpha: .15);
    line-height: 1.15;
    &.error {
      @include theme() {
        background: rgba($color: theme-get('fts-danger'), $alpha: .1);
      }
    }
    .remove-email {
      margin-left: .575rem;
      font-size: .75rem;
      color: var(--fts-gray-5);
      &:hover {
        cursor: pointer;
      }
    }
  }
}
.invite-error {
  display: flex;
  align-items: center;
  margin-top: 1rem;
  margin-left: .135rem;
  font-size: .875rem;
  color: var(--fts-danger);
  .error-icon {
    margin-right: .575rem;
  }
}
/* invite link */
.invite-link-form-control {
  display: flex;
  align-items: center;
  padding: .375rem .75rem;
  border-radius: .375rem;
  background: var(--fts-gray-1);
  .invite-link-copy {
    position: relative;
    flex: 1;
    margin-right: .375rem;
    font-size: .75rem;
    word-break: break-all;
    color: var(--fts-gray-5);
  }
  .invite-link-copy-hint {
    @include absolute-center-y;
    right: 0;
    padding: .375rem;
    box-shadow: var(--fts-elevation-3);
    border-radius: .25rem;
    background: var(--fts-popover-background);
    color: var(--fts-text);
    font-size: .75rem;
  }
}
</style>